import { Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { abbreviateNumber, AbbreviationUnit } from './_abbreviate-number';

/**
 * Rendering mode for `SgUnitsCountPipe`. See `SgUnitsCountPipe` for the selection guide
 * and per-mode rules.
 */
export type SgUnitsCountMode = 'full' | 'abbreviated';

/**
 * Formats a **count of units of something** — share quantities, contract sizes, unit
 * holdings, anything that can be fractional (e.g. `5.3 units of Apple`).
 *
 * For **integer counts of discrete entities** (clients, loans, securities-as-objects),
 * use `SgEntityCountPipe` (`sgEntityCount`) instead. That one is threshold-driven; this
 * one is mode-driven, like `sgSmartCurrency`.
 *
 * ## Mode selection
 *
 * | Mode            | When to use                                          | Example      |
 * |-----------------|------------------------------------------------------|--------------|
 * | `'full'`        | Detail views, reconciliation, exact-value surfaces   | `1,238,472`  |
 * | `'abbreviated'` | KPI tiles, dashboard cards, headline numbers         | `1.24M`      |
 *
 * Default is `'full'`. The mode is a **call-site decision driven by available space**,
 * not by the value's magnitude — `1,238,472` is correct in a detail row, `1.24M` is
 * correct in a KPI tile, for the same underlying value.
 *
 * ## Full mode rules
 *
 * - Locale-aware thousands separator (`847,234`, `1,238,472.45`).
 * - Decimals: up to 4dp, trailing zeros stripped (`5.3 → 5.3`, `5.0 → 5`,
 *   `1234.5678 → 1,234.5678`, `1234.56789 → 1,234.5679` half-expand rounded).
 *
 * ## Abbreviated mode rules — spec §6
 *
 * - `< 1,000,000`: renders as full (per spec — "Full with thousands separator").
 * - `1M ≤ v < 1B`: 2dp at the M tier, trailing zeros stripped (`1.24M`, `1.5M`, `1M`).
 * - `1B ≤ v < 1T`: 2dp at the B tier (`2.61B`).
 * - `≥ 1T`: 2dp at the T tier (`1.5T`).
 *
 * In abbreviated tiers the digits after the `.` are tier sub-units (millions inside `4.23B`),
 * not decimals of the underlying quantity — so they aren't governed by the 4dp full-mode rule.
 *
 * ## Special values
 *
 * - `null` / `undefined` / `NaN` / `±Infinity` → `—` (em dash, U+2014).
 * - Negative quantities use U+2212 minus before the number (`−1,234.5`, `−1.24M`).
 *
 * ## Usage
 *
 * Template:
 * ```html
 * <!-- Default 'full' — detail view, reconciliation, exact quantity -->
 * {{ row.shareQuantity \| sgUnitsCount }}
 *
 * <!-- Explicit 'abbreviated' — KPI tile / dashboard card -->
 * {{ kpi.totalShares \| sgUnitsCount : 'abbreviated' }}
 * ```
 *
 * Static (for non-template callers):
 * ```ts
 * SgUnitsCountPipe.transform(5.3);                    // '5.3'
 * SgUnitsCountPipe.transform(847234);                 // '847,234'
 * SgUnitsCountPipe.transform(1238472, 'abbreviated'); // '1.24M'
 * SgUnitsCountPipe.transform(2610000000, 'abbreviated'); // '2.61B'
 * SgUnitsCountPipe.transform(null);                   // '—'
 * ```
 */
@Pipe({
  name: 'sgUnitsCount',
  standalone: false,
})
export class SgUnitsCountPipe implements PipeTransform {
  constructor(@Inject(LOCALE_ID) private readonly locale: string) {}

  transform(value: number | null | undefined, mode: SgUnitsCountMode = 'full'): string {
    return SgUnitsCountPipe.transform(value, mode, this.locale);
  }

  /**
   * Static variant — same behavior, no DI required.
   *
   * @param value   Unit count (may be fractional). Non-finite → `'—'`.
   * @param mode    `'full'` (default) or `'abbreviated'`.
   * @param locale  BCP-47 locale code. Defaults to `'en-US'`.
   *
   * @example
   * SgUnitsCountPipe.transform(5.3);                       // '5.3'
   * SgUnitsCountPipe.transform(847234);                    // '847,234'
   * SgUnitsCountPipe.transform(1238472, 'abbreviated');    // '1.24M'
   * SgUnitsCountPipe.transform(1234.56789);                // '1,234.5679' (rounded to 4dp)
   * SgUnitsCountPipe.transform(-2450);                     // '−2,450'
   * SgUnitsCountPipe.transform(null);                      // '—'
   */
  static transform(
    value: number | null | undefined,
    mode: SgUnitsCountMode = 'full',
    locale: string = 'en-US'
  ): string {
    if (value == null || !Number.isFinite(value)) {
      return '—';
    }

    const absValue = Math.abs(value);

    // Spec §6: in abbreviated mode, `< 1M` still renders as full. Same formatter either way.
    if (mode === 'full' || absValue < 1_000_000) {
      return formatFull(value, locale);
    }

    const sign = value < 0 ? '−' : '';

    return `${sign}${abbreviateNumber(absValue, unitsAbbreviatedDecimals, locale)}`;
  }
}

// Locale-aware thousands separator, up to 4dp, trailing zeros auto-stripped via
// `minimumFractionDigits: 0`. Negative sign normalized to U+2212.
function formatFull(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
    useGrouping: true,
  })
    .format(value)
    .replace(/-/g, '−');
}

// Spec §6 tier rule for abbreviated mode: 2dp at M / B / T, trailing zeros stripped
// (Intl.NumberFormat with `minimumFractionDigits: 0` inside `abbreviateNumber`).
// The K tier is unreachable here — caller already short-circuited values < 1M.
function unitsAbbreviatedDecimals(_unitValue: number, _unit: AbbreviationUnit): number {
  return 2;
}
